import { z } from 'zod';

import { isDecimalString } from './quantity.js';
import { isVocabId } from './vocab-id.js';

/**
 * An i18n-ready human string. Either a bare default-locale string, or a
 * `{ locale: string }` map resolved via the standard locale chain (user
 * preference → browser/OS → English). Mirrors `LocalizedString` in
 * `03a-entities.md §5`. Defined here (not `pack.ts`) because both
 * `PredicateDeclaration` and `TraitDeclaration` carry localized labels and
 * `pack.ts` already imports from this module (avoids a circular import).
 */
export const LocalizedStringSchema = z.union([
  z.string(),
  z.record(z.string().min(1), z.string()),
]);
export type LocalizedString = z.infer<typeof LocalizedStringSchema>;

/**
 * The default locale. ADR-0004 §1: a mandatory `LocalizedString` name is
 * satisfied when *at least the default locale is present*; other locales
 * fall back through the standard chain. Mirrors `resolveLocalized`'s
 * `defaultLocale = 'en'` default in `@dl/calendar`.
 */
export const DEFAULT_LOCALE = 'en';

/**
 * Does `value` carry a non-empty default-locale name? A bare string is the
 * default-locale string itself (always satisfies); a record satisfies only
 * when it has a non-empty `DEFAULT_LOCALE` key. Used to enforce the
 * mandatory-name rule (ADR-0004 §1): identity is opaque, but a human-facing
 * name in at least the default locale is required so the raw id never leaks
 * into the UI.
 */
export function hasDefaultLocaleName(value: unknown): value is LocalizedString {
  if (typeof value === 'string') return value.length > 0;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const rec = value as Record<string, unknown>;
    const v = rec[DEFAULT_LOCALE];
    return typeof v === 'string' && v.length > 0;
  }
  return false;
}

/**
 * A **mandatory** localized display name (ADR-0004 §1). Unlike the bare
 * `LocalizedStringSchema` (which a predicate's *optional* `description`
 * uses), this rejects a value that lacks a non-empty default-locale string:
 * a bare `''` or a `{ fr: '…' }`-only record fails. Open-vocabulary
 * declarations (predicate / trait / tuple field / enum value) carry one of
 * these in their `label` field — identity is the opaque id, naming is this.
 */
export const RequiredLocalizedNameSchema = LocalizedStringSchema.refine(hasDefaultLocaleName, {
  message: `name must include a non-empty default-locale ("${DEFAULT_LOCALE}") string`,
});

/**
 * Schema for an opaque, minted vocabulary id of `predicate` kind
 * (`pred_<uuidv4>`, ADR-0004 §1/§6). The id is **authored by the minter**
 * (`mintVocabId('predicate')`), never typed by a human or the LLM; it is
 * decoupled from naming and never user-facing. Kept as a validated string
 * (not a branded type) to stay JSON-serialisable through the op log.
 */
export const PredicateIdSchema = z
  .string()
  .refine((s) => isVocabId('predicate', s), {
    message: 'predicate id must be a minted opaque id (pred_<uuidv4>) — see mintVocabId',
  });

/** Schema for an opaque, minted `enum` value id (`enm_<uuidv4>`, ADR-0004 §1). */
export const EnumValueIdSchema = z
  .string()
  .refine((s) => isVocabId('enum', s), {
    message: 'enum value id must be a minted opaque id (enm_<uuidv4>) — see mintVocabId',
  });

/** Schema for an opaque, minted tuple-field id (`fld_<uuidv4>`, ADR-0004 §1). */
export const TupleFieldIdSchema = z
  .string()
  .refine((s) => isVocabId('field', s), {
    message: 'tuple field id must be a minted opaque id (fld_<uuidv4>) — see mintVocabId',
  });

/**
 * Schema for an opaque, minted measurement-`dimension` id (`dim_<uuidv4>`,
 * ADR-0007 §2). Like every other open-vocabulary id, it is minted, never
 * typed by a human or the LLM — the readable token (`metric`/`length`) is
 * gone, identity is the opaque id, naming is the definition's `name`.
 */
export const DimensionIdSchema = z
  .string()
  .refine((s) => isVocabId('dimension', s), {
    message: 'dimension id must be a minted opaque id (dim_<uuidv4>) — see mintVocabId',
  });

/** Schema for an opaque, minted `unit` id (`unit_<uuidv4>`, ADR-0007 §2). */
export const UnitIdSchema = z
  .string()
  .refine((s) => isVocabId('unit', s), {
    message: 'unit id must be a minted opaque id (unit_<uuidv4>) — see mintVocabId',
  });

/**
 * The value kinds the substrate understands at Walking Skeleton scope.
 *
 * - `real_datetime` — ISO 8601 wall-clock instant. The validator requires
 *   an explicit offset (Z or ±HH:MM); naive timestamps are rejected.
 * - `real_datetime_range` — pair of ISO 8601 instants `{ from, to }`,
 *   both with explicit offsets, `to >= from`. Used for wall-clock
 *   ranges such as `Session.real_world_span`. Field names match the
 *   in-world `date_range` shape in `03a-entities.md §7`.
 *
 * - `rich_text` — markdown / structured prose. Stored as a string at
 *   substrate level (the renderer is a UI concern). The value-kind
 *   distinction lets the entity-page UI pick the rich editor; the
 *   auditor can lint on emptiness for predicates like
 *   `campaign.setting_summary`.
 * - `enum` — closed set; allowed values declared via
 *   `PredicateDeclaration.enum_values` (a required field when this kind
 *   is declared). Validators check membership at write time.
 * - `asset` — asset-identifier stand-in (Phase 1: opaque string id;
 *   Phase 2: bound to the asset metadata table via FK-shaped lookup).
 *   The value-kind distinction lets the UI surface the upload affordance
 *   instead of a text input.
 *
 * The canonical-day-count (in-world) calendar value-kind family lands with
 * the `@dl/calendar` runtime in Phase 1 (06-timeline.md); the wall-clock
 * kinds here are the substrate boundary that bridges in-world time to UI
 * formatting using the viewer's timezone (`Intl`).
 */
export type ValueKind =
  | 'text'
  | 'rich_text'
  | 'number'
  | 'boolean'
  | 'ref'
  | 'enum'
  | 'asset'
  // Media (asset-backed; the value is an asset id, declaration constrains
  // accepted formats + size via `media`). `image` is the portrait/hero case
  // restricted to PNG/WEBP/JPEG by default; `file` is the generic upload
  // case with selectable format buckets. See `03a-entities.md §8`.
  | 'image'
  | 'file'
  // Real-world wall-clock kinds. `real_datetime`/`real_datetime_range`
  // predate this work; `real_date` (date-only) and `real_time` (time-only)
  // round out the real-world family the GM authoring surfaces need.
  | 'real_date'
  | 'real_time'
  | 'real_datetime'
  | 'real_datetime_range'
  // In-world (calendar) kinds. The value carries a canonical-day-count
  // `WorldTime` (06-timeline.md §6) the `@dl/calendar` runtime renders.
  // Three kinds match the GM's mental model (date / datetime / time-of-day);
  // they differ in which `WorldTime` components are meaningful.
  | 'game_date'
  | 'game_datetime'
  | 'game_time'
  // In-world calendar RANGE kinds (06-timeline.md §7, #221): a `{ from, to }`
  // pair of in-world bounds over the shared canonical-day axis — the in-world
  // analogue of `real_datetime_range`. Lets a single value carry an in-world
  // span (a reign, a war, a festival window) instead of two separate facts.
  | 'game_date_range'
  | 'game_datetime_range'
  // A structured, named-field record — qualifies each entry of a fact
  // (e.g. pair a `ref` with a `role`). Declaration carries `tuple_fields`.
  | 'tuple'
  // An sRGB hex color (`#rrggbb` / `#rrggbbaa`, case-insensitive), stored as
  // the literal hex string. Used by content facts and reused by the
  // `accent_color` campaign setting (ADR-0004 §6). The value-kind
  // distinction lets the UI surface a color picker (ticket #182).
  | 'color'
  // Spatial geometry kinds (05-geography.md §2/§12, #202). Coordinates are
  // expressed in the PARENT FRAME's units — there is no global CRS. A point
  // is a precise position; a line is a 1-D extent (routes / borders); a
  // polygon is a region (with optional interior holes). Every geometry value
  // carries an optional `alt_description` (rich_text) — visual content a
  // screen reader cannot describe from coordinates alone (§12).
  | 'geometry_point'
  | 'geometry_line'
  | 'geometry_polygon'
  // A Location's renderable coordinate space (05-geography.md §3, #203):
  // `{ bounds, units, origin, grid?, background? }`. It needs its own kind
  // (not a `tuple`) because it nests a `GridDefinition`, which `tuple_fields`
  // cannot express. The frame is validity-aware — replacing bounds supersedes
  // the prior frame via the existing fact-validity mechanism (no new
  // mechanism); a Location without one simply isn't directly renderable yet.
  | 'interior_frame'
  // Multi-context containment (05-geography.md §2, #206): a structured
  // `{ context, parent, position? }` record carried by the `contained_in`
  // predicate. `parent` is a Location ref; `context` is a FREE string (NOT
  // declared vocabulary — the ADR-0004 opaque-id rule applies to the
  // predicate, not the value's context label) defaulting to `geographic` when
  // omitted; `position` is an optional point / line / polygon expressed in the
  // parent's interior frame. A Location may carry several containment facts at
  // once, one per (parent, context) — geographic + political + cultural
  // simultaneously — each its own independently-dated / revealed fact. It is
  // its own kind (not a `tuple`) because `position` nests a discriminated
  // geometry union that `tuple_fields` cannot express.
  | 'containment'
  // A recurrence rule (06-timeline.md §10.1): the object of a `recurs_as`
  // fact on a parent Lore. Carries `{ cycle, every_n, anchor, at_location?,
  // participants?, duration? }` with `anchor: AnchorSpec`. Stored opaquely;
  // the `@dl/calendar` engine synthesizes virtual instances from it (B3). The
  // value-kind distinction lets the editor surface the anchor-authoring
  // control (ticket #238 / B5).
  | 'recurrence_rule';

export const ValueKindSchema = z.enum([
  'text',
  'rich_text',
  'number',
  'boolean',
  'ref',
  'enum',
  'asset',
  'image',
  'file',
  'real_date',
  'real_time',
  'real_datetime',
  'real_datetime_range',
  'game_date',
  'game_datetime',
  'game_time',
  'game_date_range',
  'game_datetime_range',
  'tuple',
  'color',
  'geometry_point',
  'geometry_line',
  'geometry_polygon',
  'interior_frame',
  'containment',
  'recurrence_rule',
]);

/**
 * In-world time precision (06-timeline.md §6). Controls how much of a
 * `WorldTime` value renders ("year 1067" vs "3rd of Hammer, 10:32").
 */
export const PrecisionSchema = z.enum([
  'year',
  'season',
  'month',
  'day',
  'hour',
  'minute',
  'second',
]);
export type Precision = z.infer<typeof PrecisionSchema>;

/**
 * ISO 8601 timestamp that REQUIRES an explicit offset (`Z` or `±HH:MM`).
 * Zod 4's `z.iso.datetime({ offset: true })` accepts the offset; combined
 * with `local: false` (default) it rejects naive timestamps.
 */
const RealDatetimeSchema = z.iso.datetime({ offset: true });

/**
 * Wall-clock range value: `{ from, to }`, both ISO 8601 with explicit
 * offsets, `to >= from`. Used for predicates like `Session.real_world_span`
 * (`03a-entities.md §7`). Field names match the in-world `date_range`
 * shape so a polymorphic `real_datetime_range | date_range` predicate
 * reads consistently.
 */
export const RealDatetimeRangeSchema = z
  .object({
    from: RealDatetimeSchema,
    to: RealDatetimeSchema,
  })
  .strict()
  .refine(({ from, to }) => Date.parse(to) >= Date.parse(from), {
    message: 'real_datetime_range: to must be >= from',
  });
export type RealDatetimeRange = z.infer<typeof RealDatetimeRangeSchema>;

/** Date-only ISO 8601 calendar date (`YYYY-MM-DD`), no time, no offset. */
const RealDateSchema = z.iso.date();

/** Time-of-day ISO 8601 (`HH:MM` or `HH:MM:SS`), no date, no offset. */
const RealTimeSchema = z.iso.time();

/**
 * Matches an sRGB hex color: a leading `#` followed by EITHER 6 hex digits
 * (`#rrggbb`) OR 8 hex digits (`#rrggbbaa`, with alpha). Case-insensitive.
 * 3-/4-digit shorthand is intentionally rejected so the stored value is
 * always a normalised full-length hex string. Anchored end-to-end — no
 * leading/trailing whitespace.
 */
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Predicate guard: is `value` a valid sRGB hex color string
 * (`#rrggbb` / `#rrggbbaa`, case-insensitive)? Reusable by any consumer
 * that needs the validation rule without a Zod parse — e.g. the
 * `accent_color` campaign setting key (ADR-0004 §6, ticket #169).
 */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

/**
 * Zod schema for the `color` value kind. Accepts an sRGB hex string
 * (`#rrggbb` / `#rrggbbaa`, case-insensitive); rejects everything else.
 * Exported for reuse by config-key schemas (the `accent_color` setting
 * validates via this same rule) and the per-kind value table below.
 */
export const HexColorSchema = z
  .string()
  .regex(HEX_COLOR_RE, { message: 'color: must be an sRGB hex string (#rrggbb or #rrggbbaa)' });

// ── Spatial geometry value kinds (05-geography.md §2/§12, #202) ───────────
//
// Coordinates live in the parent frame's units; there is no global CRS
// (`05-geography.md` principle 6). Coordinates may be any finite number
// (fractional and negative are legal — the origin can sit at the centre of a
// frame, §3). The substrate stores the raw geometry; rendering / clipping is a
// renderer concern, not a validation concern here.

/**
 * A single coordinate in the parent frame's units. Both components must be
 * finite (NaN / ±Infinity rejected). Negative + fractional are valid — a
 * `center`-origin frame places (0,0) at the middle, so coords go negative.
 */
export const GeometryPointCoordSchema = z
  .object({
    x: z.number().finite(),
    y: z.number().finite(),
  })
  .strict();
export type GeometryPointCoord = z.infer<typeof GeometryPointCoordSchema>;

/**
 * Optional accessibility description carried by every geometry value
 * (`05-geography.md §12`). Polygons / lines / routes are visual content a
 * screen reader cannot describe from coordinates alone; the authored
 * `alt_description` is the SR fallback and the map-tooltip text. Stored as a
 * `rich_text` string at substrate level (renderer is a UI concern), mirroring
 * the `rich_text` value kind. Optional — the auditor flags geometry-bearing
 * facts on revealed Maps that lack one as a low-severity advisory (§12), but
 * the substrate never blocks.
 */
const GeometryAltDescriptionSchema = z.string().min(1).optional();

/**
 * Zod schema for the `geometry_point` value kind: a precise position
 * (`05-geography.md §12` — "a point is a precise position"). Carries the
 * coordinate inline plus the optional `alt_description`.
 */
export const GeometryPointValueSchema = z
  .object({
    x: z.number().finite(),
    y: z.number().finite(),
    alt_description: GeometryAltDescriptionSchema,
  })
  .strict();
export type GeometryPointValue = z.infer<typeof GeometryPointValueSchema>;

/**
 * Zod schema for the `geometry_line` value kind: a 1-D extent — an ordered
 * polyline of ≥2 points (routes / borders, `05-geography.md §9/§12`). A line
 * with fewer than two points has no extent and is rejected.
 */
export const GeometryLineValueSchema = z
  .object({
    points: z.array(GeometryPointCoordSchema).min(2),
    alt_description: GeometryAltDescriptionSchema,
  })
  .strict();
export type GeometryLineValue = z.infer<typeof GeometryLineValueSchema>;

/**
 * Zod schema for the `geometry_polygon` value kind: a region — a closed ring
 * of ≥3 points, with optional interior `holes` (each itself a ≥3-point ring),
 * `05-geography.md §2/§12`. The ring is implicitly closed (the first point is
 * not repeated). The obsolete `map?: MapId` field (`05-geography.md §14`) is
 * intentionally NOT present — geometry no longer references a map.
 */
export const GeometryPolygonValueSchema = z
  .object({
    points: z.array(GeometryPointCoordSchema).min(3),
    holes: z.array(z.array(GeometryPointCoordSchema).min(3)).optional(),
    alt_description: GeometryAltDescriptionSchema,
  })
  .strict();
export type GeometryPolygonValue = z.infer<typeof GeometryPolygonValueSchema>;

// ── interior_frame value kind (05-geography.md §3, #203) ──────────────────

/**
 * An overlay grid on an interior frame (`05-geography.md §3`). `cell_size` is
 * expressed in the frame's units and must be positive; `origin_offset` is a
 * sub-cell alignment nudge (any finite coords).
 */
export const GridDefinitionSchema = z
  .object({
    kind: z.enum(['square', 'hex_pointy', 'hex_flat']),
    cell_size: z.number().finite().positive(),
    origin_offset: GeometryPointCoordSchema.optional(),
  })
  .strict();
export type GridDefinition = z.infer<typeof GridDefinitionSchema>;

/**
 * Zod schema for the `interior_frame` value kind (`05-geography.md §3`): a
 * Location's renderable coordinate space.
 *
 *   - `bounds` — canvas dimensions in `units`; both `w` and `h` must be
 *     positive (a zero / negative extent has no renderable area).
 *   - `units` — declared scale label (`"meters"`, `"feet"`, `"stylized"`,
 *     `"pixels"`, …); a free, non-empty string (an open vocabulary, not a
 *     declared unit id — the frame's scale commitment is descriptive).
 *   - `origin` — where (0,0) sits: `"corner"` (image-aligned) or `"center"`
 *     (symmetric maps). Rejecting any other token is part of the AC.
 *   - `grid?` — optional tactical overlay (`GridDefinition`).
 *   - `background?` — optional default background image (an asset id string).
 *
 * Validity-awareness (city walls expand → new bounds supersede the prior
 * frame) is the EXISTING fact-validity mechanism — a new `interior_frame`
 * fact with `valid_from`, the prior one closed with `valid_to`; nothing in the
 * value shape encodes time.
 */
export const InteriorFrameValueSchema = z
  .object({
    bounds: z
      .object({
        w: z.number().finite().positive(),
        h: z.number().finite().positive(),
      })
      .strict(),
    units: z.string().min(1),
    origin: z.enum(['corner', 'center']),
    grid: GridDefinitionSchema.optional(),
    // Asset id (string) into the asset store — same substrate-level shape as
    // the `asset` / `image` value kinds (an opaque id; the upload surface and
    // asset metadata table bind it). Optional.
    background: z.string().min(1).optional(),
  })
  .strict();
export type InteriorFrameValue = z.infer<typeof InteriorFrameValueSchema>;

// ── containment value kind (05-geography.md §2, #206) ─────────────────────

/**
 * The default containment context (`05-geography.md §2.1`): "when omitted from
 * a `contained_in` fact, the context defaults to `geographic`." Almost every
 * Location has a geographic parent, so the default keeps the common case
 * lightweight (the GM authors `{ parent }` and gets `{ context: 'geographic',
 * parent }`).
 */
export const DEFAULT_CONTAINMENT_CONTEXT = 'geographic';

/**
 * The optional `position` of a containment (`05-geography.md §2`): a geometry
 * expressed in the PARENT's interior frame. A discriminated union over the
 * three geometry kinds, reusing the #202 inner coordinate validation so a
 * containment position obeys exactly the same coordinate rules as a free
 * geometry fact (finite coords; ≥2 points for a line, ≥3 for a polygon ring).
 * Carries no `alt_description` of its own — the containment *fact* is the
 * a11y-describable unit, not the embedded position.
 *
 *   - `point`   — `{ kind: 'point',   value: { x, y } }`         (precise spot)
 *   - `line`    — `{ kind: 'line',    value: Point[] }`          (≥2 points)
 *   - `polygon` — `{ kind: 'polygon', value: Point[], holes? }`  (≥3-pt ring)
 */
export const PositionGeometrySchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('point'),
      value: GeometryPointCoordSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('line'),
      value: z.array(GeometryPointCoordSchema).min(2),
    })
    .strict(),
  z
    .object({
      kind: z.literal('polygon'),
      value: z.array(GeometryPointCoordSchema).min(3),
      holes: z.array(z.array(GeometryPointCoordSchema).min(3)).optional(),
    })
    .strict(),
]);
export type PositionGeometry = z.infer<typeof PositionGeometrySchema>;

/**
 * Zod schema for the `containment` value kind (`05-geography.md §2`):
 * `{ context, parent, position? }`.
 *
 *   - `parent`   — a Location entity id (a bare ref string; the ref-root
 *     advisory is the auditor's job, not a parse concern — mirrors the bare
 *     `ref` kind which never constrains the namespace at parse time).
 *   - `context`  — a FREE, non-empty string (open vocabulary; NOT declared
 *     vocab — §2.1). Defaults to `DEFAULT_CONTAINMENT_CONTEXT` ('geographic')
 *     when OMITTED, via Zod `.default(...)` — an authored `{ parent }` parses
 *     to `{ context: 'geographic', parent }`. An explicit empty-string context
 *     is rejected (a blank context is a mistake, not "the default").
 *   - `position` — optional `PositionGeometry` in the parent's interior frame.
 *
 * `.strict()` rejects unknown keys so a malformed payload (a stray `kind`, a
 * typo'd field) surfaces as an invalid fact rather than silently persisting.
 */
export const ContainmentValueSchema = z
  .object({
    parent: z.string().min(1),
    context: z.string().min(1).default(DEFAULT_CONTAINMENT_CONTEXT),
    position: PositionGeometrySchema.optional(),
  })
  .strict();
export type ContainmentValue = z.infer<typeof ContainmentValueSchema>;

/**
 * Read the effective context of a containment value: its explicit `context`
 * when a non-empty string is present, else the `geographic` default (§2.1).
 * The "contains" backlink groups by this — a value parsed through
 * {@link ContainmentValueSchema} already carries the default, but a raw stored
 * value (or a hand-built record) might omit it, so the read is defensive.
 */
export function containmentContext(value: unknown): string {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const ctx = (value as Record<string, unknown>)['context'];
    if (typeof ctx === 'string' && ctx.length > 0) return ctx;
  }
  return DEFAULT_CONTAINMENT_CONTEXT;
}

/**
 * Read the parent (a Location entity id) out of a containment value, or `null`
 * when the value is not a well-formed containment record. The single
 * chokepoint the auditor's reference-integrity rules (`ref.dangling` /
 * `ref.wrong_type`) and the "contains" backlink read through, so the embedded
 * `parent` ref is treated exactly like a bare `ref` value despite living one
 * level down inside the structured value (#206 migrated `contained_in` from a
 * bare `ref` to this structured form).
 */
export function containmentParent(value: unknown): string | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const parent = (value as Record<string, unknown>)['parent'];
    if (typeof parent === 'string' && parent.length > 0) return parent;
  }
  return null;
}

/**
 * Resolve the entity id a fact's value REFERENCES, given its declaration —
 * the single chokepoint for reference-integrity checks (`ref.dangling` /
 * `ref.wrong_type`) and computed backlinks across the *two* shapes a referent
 * can hide in:
 *
 *   - a bare `ref` predicate — the value IS the entity-id string.
 *   - a `containment` predicate (#206) — the referent is `value.parent`.
 *
 * Returns `null` when the predicate references nothing, or the value is the
 * wrong shape (a non-string ref, a malformed containment). Keeps the auditor
 * rules value-kind-agnostic: they ask "what does this fact point at?" without
 * special-casing containment inline, so a future ref-bearing kind is added
 * here once rather than in every rule.
 */
export function factRefTarget(decl: PredicateDeclaration, value: unknown): string | null {
  if (decl.value_kind.includes('ref')) {
    return typeof value === 'string' && value.length > 0 ? value : null;
  }
  if (decl.value_kind.includes('containment')) {
    return containmentParent(value);
  }
  return null;
}

/**
 * A `number` value is EITHER a bare JS `number` (unitless) OR a
 * **dimensioned** value (08-units.md, ADR-0007 §1/§3). A dimensioned value
 * stores its dimension's **canonical base value** as an exact decimal string
 * (`canonical_value`) plus the authoring/display `unit_id` as provenance —
 * exactly as `game_date` stores `{ day, calendar_id }`, where the calendar
 * is provenance + display default, not the identity of the value. The
 * `unit_id` is an opaque minted `unit_…` id (see `units.ts`); the substrate
 * stores it opaquely (the unit registry lives at campaign scope) and never
 * mints/types it. `canonical_value` is owned by the `quantity.ts` chokepoint
 * — never a binary float, so the micro→macro span and currency stay exact.
 */
export const DimensionedValueSchema = z
  .object({
    canonical_value: z.string().refine(isDecimalString, {
      message: 'canonical_value must be a decimal string (see quantity.ts)',
    }),
    unit_id: UnitIdSchema,
  })
  .strict();
export type DimensionedValue = z.infer<typeof DimensionedValueSchema>;
const NumberValueSchema = z.union([z.number(), DimensionedValueSchema]);

/**
 * The normalised view of a `number`-kind value: a unitless `plain` number,
 * or a `dimensioned` canonical value + provenance unit. `null` if the value
 * is neither shape.
 */
export type NumberValue =
  | { kind: 'plain'; value: number }
  | { kind: 'dimensioned'; canonical_value: string; unit_id: string };

/** Normalise a `number`-kind value to its {@link NumberValue} view. */
export function readNumberValue(value: unknown): NumberValue | null {
  if (typeof value === 'number') return { kind: 'plain', value };
  const parsed = DimensionedValueSchema.safeParse(value);
  if (parsed.success) {
    return {
      kind: 'dimensioned',
      canonical_value: parsed.data.canonical_value,
      unit_id: parsed.data.unit_id,
    };
  }
  return null;
}

/**
 * In-world calendar time, canonical-day-count form (06-timeline.md §6).
 * `day` is the integer source of truth (days from the campaign epoch);
 * `seconds_into_day` (0..86399) carries time-of-day; `precision` controls
 * render granularity. The three game kinds reuse this shape, differing in
 * which components are required:
 *   - `game_date`     — `day` required, no `seconds_into_day`.
 *   - `game_datetime` — `day` + `seconds_into_day` required.
 *   - `game_time`     — `seconds_into_day` required, no `day` (time-of-day).
 */
const GameDateSchema = z
  .object({
    day: z.number().int(),
    precision: PrecisionSchema.optional(),
  })
  .strict();

const GameDatetimeSchema = z
  .object({
    day: z.number().int(),
    seconds_into_day: z.number().int().min(0).max(86399),
    precision: PrecisionSchema.optional(),
  })
  .strict();

const GameTimeSchema = z
  .object({
    seconds_into_day: z.number().int().min(0).max(86399),
    precision: PrecisionSchema.optional(),
  })
  .strict();

export type GameDate = z.infer<typeof GameDateSchema>;
export type GameDatetime = z.infer<typeof GameDatetimeSchema>;
export type GameTime = z.infer<typeof GameTimeSchema>;

/**
 * In-world calendar RANGE kinds (06-timeline.md §7, ADR-0006, #221). A pair of
 * in-world bounds over the shared canonical-day axis — the in-world analogue of
 * `real_datetime_range`. Each bound reuses the corresponding scalar shape, so a
 * per-bound `precision` is preserved (e.g. a war known to the year on `from`
 * but the day on `to`). The value records the bounds; half-open interval
 * semantics are a query-time concern, not stored. `to` must not precede `from`
 * on the canonical axis.
 *   - `game_date_range`     — `{ from: GameDate, to: GameDate }`.
 *   - `game_datetime_range` — `{ from: GameDatetime, to: GameDatetime }`.
 */
const GameDateRangeSchema = z
  .object({ from: GameDateSchema, to: GameDateSchema })
  .strict()
  .refine(({ from, to }) => to.day >= from.day, {
    message: 'game_date_range: to must be >= from on the canonical day axis',
  });

const GameDatetimeRangeSchema = z
  .object({ from: GameDatetimeSchema, to: GameDatetimeSchema })
  .strict()
  .refine(
    ({ from, to }) =>
      to.day > from.day || (to.day === from.day && to.seconds_into_day >= from.seconds_into_day),
    { message: 'game_datetime_range: to must be >= from on the canonical axis' },
  );

export type GameDateRange = z.infer<typeof GameDateRangeSchema>;
export type GameDatetimeRange = z.infer<typeof GameDatetimeRangeSchema>;

/**
 * The `recurrence_rule` value kind (06-timeline.md §10.1). The object of a
 * `recurs_as` fact on a parent Lore: a rule that synthesizes virtual event
 * instances on demand (festivals, anniversaries, eclipses) without instance
 * explosion. The substrate stores the rule opaquely and round-trips it
 * through the op log / replica fold / value-kind-agnostic projection — it
 * never interprets it (the cycle/anchor runtime is the `@dl/calendar`
 * engine, B-track). This module owns only the shape + validation.
 *
 * `AnchorSpec` is the discriminated union of *where within the named cycle*
 * the rule fires:
 *  - `day_of_cycle`    — a fixed `offset` into the cycle (day 184 of the year).
 *  - `nested_position` — a `path` of `(cycle, instance)` steps drilling into
 *    nested cycles (month="Reaping" → day-of-month=25). `instance` is a name
 *    (string) or an ordinal (number) per §10.1.
 *  - `labeled`         — fires whenever a named cycle's labeled position
 *    recurs (stem-branch-year label "Wood Rat").
 *
 * `at_location` / `participants` are `ref`-shaped (entity ids); they default
 * onto each synthesized instance. `duration` carries an optional `days`
 * count and an optional intra-day `sub_day`.
 *
 * **Divergence #2 (decomposition flag).** The §10.1 design types
 * `duration.sub_day` as the rich GM-declared `SubDay` watch/bell period map
 * (§6). The as-built intra-day model is a fixed `seconds_into_day` integer
 * (D15/D18 — the §6 `SubDayPeriodDefinition` vocabulary was never built), so
 * `sub_day` **degrades to** `{ seconds_into_day }` here, mirroring the
 * shipped `game_datetime` / `game_time` kinds rather than restoring the §6
 * vocabulary. Flagged, not silently aligned: when the §6 sub-day vocabulary
 * lands, this nests it instead.
 */
const ANCHOR_KINDS = ['day_of_cycle', 'nested_position', 'labeled'] as const;
export const AnchorKindSchema = z.enum(ANCHOR_KINDS);
export type AnchorKind = z.infer<typeof AnchorKindSchema>;

/** One step of a `nested_position` anchor path: a cycle + the instance within it. */
const NestedAnchorStepSchema = z
  .object({
    cycle: z.string().min(1),
    // §10.1: instance is addressed by name (e.g. "Reaping") or ordinal
    // (e.g. day-of-month = 25). Both forms are accepted opaquely; the
    // calendar engine resolves them against the declared cycle.
    instance: z.union([z.string().min(1), z.number().int()]),
  })
  .strict();

/**
 * `AnchorSpec` (§10.1) — the discriminated union of anchor flavors. A
 * non-member `kind` (unknown anchor) fails the union; each variant is
 * `.strict()` so a stray field is rejected too.
 */
export const AnchorSpecSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('day_of_cycle'),
      offset: z.number().int(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('nested_position'),
      path: z.array(NestedAnchorStepSchema).min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal('labeled'),
      cycle: z.string().min(1),
      label: z.string().min(1),
    })
    .strict(),
]);
export type AnchorSpec = z.infer<typeof AnchorSpecSchema>;

/**
 * Intra-day duration component. Degrades the §10.1 `SubDay` to the as-built
 * fixed `seconds_into_day` (Divergence #2) — same shape as `game_datetime`.
 */
const RecurrenceSubDaySchema = z
  .object({
    seconds_into_day: z.number().int().min(0).max(86399),
  })
  .strict();

/** `duration` — optional whole-day count + optional intra-day position. */
const RecurrenceDurationSchema = z
  .object({
    days: z.number().int().min(0).optional(),
    sub_day: RecurrenceSubDaySchema.optional(),
  })
  .strict();

/**
 * The `recurrence_rule` value: a `RecurrenceRule` (§10.1). `every_n` is a
 * positive integer (1 = every cycle, 2 = every other); a non-positive
 * `every_n` is rejected. `at_location` / `participants` are entity-id refs
 * (the same non-empty-string shape as the `ref` kind — root membership is an
 * advisory auditor concern, not enforced here).
 */
export const RecurrenceRuleSchema = z
  .object({
    cycle: z.string().min(1),
    every_n: z.number().int().positive(),
    anchor: AnchorSpecSchema,
    at_location: z.string().min(1).optional(),
    participants: z.array(z.string().min(1)).optional(),
    duration: RecurrenceDurationSchema.optional(),
  })
  .strict();
export type RecurrenceRule = z.infer<typeof RecurrenceRuleSchema>;

/**
 * A `tuple` value is a plain named-field record. The substrate-level schema
 * only asserts "object"; the per-field kind/enum/optional checks happen in
 * `validateValueWithDeclaration` against the declaration's `tuple_fields`
 * (the declaration is needed to know the field shape).
 */
const TupleValueSchema = z.record(z.string().min(1), z.unknown());

/** Per-kind value schemas. */
export const ValueSchemaByKind = {
  text: z.string(),
  // Rich text is stored as a string at the substrate level; UI picks
  // the editor (markdown / prose-mirror / etc) via `rich_text_mode`.
  rich_text: z.string(),
  // A bare number (unitless) OR a dimensioned canonical value — see
  // DimensionedValueSchema (ADR-0007).
  number: NumberValueSchema,
  boolean: z.boolean(),
  // ref is an entity id; we don't constrain the namespace here.
  ref: z.string().min(1),
  // `enum` is membership-validated against `PredicateDeclaration.enum_values`
  // at the validateValue boundary — see below. The raw schema only checks
  // it is a non-empty string.
  enum: z.string().min(1),
  // `asset` / `image` / `file` are asset identifiers at substrate scope
  // (string id into the asset store). The declaration's `media` block
  // constrains accepted formats + size; that constraint is advisory, like
  // ref-root membership (the auditor warns, the substrate persists).
  asset: z.string().min(1),
  image: z.string().min(1),
  file: z.string().min(1),
  // Real-world wall-clock kinds.
  real_date: RealDateSchema,
  real_time: RealTimeSchema,
  // real_datetime is an ISO 8601 timestamp with explicit offset.
  real_datetime: RealDatetimeSchema,
  real_datetime_range: RealDatetimeRangeSchema,
  // In-world calendar kinds (canonical day count).
  game_date: GameDateSchema,
  game_datetime: GameDatetimeSchema,
  game_time: GameTimeSchema,
  // In-world calendar ranges (`{ from, to }` over the canonical day axis).
  game_date_range: GameDateRangeSchema,
  game_datetime_range: GameDatetimeRangeSchema,
  // Structured record — per-field validation is declaration-aware.
  tuple: TupleValueSchema,
  // sRGB hex color string (`#rrggbb` / `#rrggbbaa`, case-insensitive).
  color: HexColorSchema,
  // Spatial geometry — coordinates in the parent frame's units (#202).
  geometry_point: GeometryPointValueSchema,
  geometry_line: GeometryLineValueSchema,
  geometry_polygon: GeometryPolygonValueSchema,
  // A Location's renderable coordinate space (#203).
  interior_frame: InteriorFrameValueSchema,
  // Multi-context containment — `{ context, parent, position? }` (#206). The
  // schema defaults an omitted context to `geographic`, so `validateValue`
  // returns the normalised value (context present).
  containment: ContainmentValueSchema,
  // Recurrence rule (`{ cycle, every_n, anchor: AnchorSpec, … }`, §10.1).
  recurrence_rule: RecurrenceRuleSchema,
} as const satisfies Record<ValueKind, z.ZodTypeAny>;

/**
 * Validate a value against a value kind. Polymorphic predicates may
 * declare multiple acceptable kinds (e.g. `occurred_at` accepts both
 * `real_datetime` and a future calendar-date kind). The first matching
 * kind wins.
 *
 * For `enum`, callers should use `validateValueWithDeclaration` which
 * also checks the value belongs to the declaration's `enum_values`.
 * The bare `validateValue(['enum'], v)` only enforces the underlying
 * string shape; it cannot reach into the declaration to check membership.
 */
export type ValidationResult<T = unknown> = { ok: true; value: T } | { ok: false; error: string };

export function validateValue(kinds: readonly ValueKind[], value: unknown): ValidationResult {
  for (const kind of kinds) {
    const result = ValueSchemaByKind[kind].safeParse(value);
    if (result.success) return { ok: true, value: result.data };
  }
  return {
    ok: false,
    error: `value does not match any of: ${kinds.join(', ')}`,
  };
}

/**
 * Validate a value with full reference to a `PredicateDeclaration` —
 * additionally enforces `enum_values` membership for `enum`-kind values.
 * Use this at the substrate write boundary where the declaration is in
 * hand (i.e. after a `PredicateRegistry.lookup`).
 */
export function validateValueWithDeclaration(
  decl: PredicateDeclaration,
  value: unknown,
): ValidationResult {
  const base = validateValue(decl.value_kind, value);
  if (!base.ok) return base;
  if (decl.value_kind.includes('enum')) {
    const allowed = decl.enum_values;
    if (!allowed || allowed.length === 0) {
      return {
        ok: false,
        error: `predicate "${decl.id}" declares enum kind but has no enum_values`,
      };
    }
    // ADR-0004 §1: a fact's `enum` value stores the option's opaque `enm_`
    // id, so membership is checked against the declared option ids.
    const asString = base.value as string;
    const allowedIds = allowed.map((o) => o.id);
    if (!allowedIds.includes(asString)) {
      return {
        ok: false,
        error: `value "${asString}" not in enum_values for predicate "${decl.id}": [${allowedIds.join(', ')}]`,
      };
    }
  }
  // Tuple: validate the record against the declared field shape. Only runs
  // when the value actually matched the tuple kind (a record); polymorphic
  // tuple|other predicates that matched another kind skip this.
  if (decl.value_kind.includes('tuple') && isPlainRecord(base.value)) {
    const tupleResult = validateTupleValue(decl, base.value);
    if (!tupleResult.ok) return tupleResult;
  }
  return base;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Validate a tuple record against a declaration's `tuple_fields`: every
 * required field present, every present field matching its kind (and enum
 * membership), no unknown fields.
 */
export function validateTupleValue(
  decl: PredicateDeclaration,
  record: Record<string, unknown>,
): ValidationResult {
  const fields = decl.tuple_fields;
  if (!fields || fields.length === 0) {
    return { ok: false, error: `predicate "${decl.id}" declares tuple kind but has no tuple_fields` };
  }
  // ADR-0004 §1: tuple records key by the field's opaque `fld_` id (the
  // field id replaces the former identity-bearing `name` token).
  const known = new Set(fields.map((f) => f.id));
  for (const key of Object.keys(record)) {
    if (!known.has(key)) {
      return { ok: false, error: `tuple field "${key}" is not declared on predicate "${decl.id}"` };
    }
  }
  for (const field of fields) {
    const present = Object.prototype.hasOwnProperty.call(record, field.id);
    if (!present) {
      if (field.optional) continue;
      return { ok: false, error: `tuple field "${field.id}" is required on predicate "${decl.id}"` };
    }
    const fieldValue = record[field.id];
    const fieldResult = validateValue([field.value_kind], fieldValue);
    if (!fieldResult.ok) {
      return { ok: false, error: `tuple field "${field.id}": ${fieldResult.error}` };
    }
    if (field.value_kind === 'enum') {
      // A tuple enum value stores the option's opaque `enm_` id.
      const allowedIds = (field.enum_values ?? []).map((o) => o.id);
      if (!allowedIds.includes(fieldValue as string)) {
        return {
          ok: false,
          error: `tuple field "${field.id}" value "${String(fieldValue)}" not in [${allowedIds.join(', ')}]`,
        };
      }
    }
  }
  return { ok: true, value: record };
}

/**
 * The closed entity-root set, re-declared here as a value-level constant so
 * `predicate.ts` does not need to import from `pack.ts` (avoids a circular
 * import — `pack.ts` already imports `PredicateDeclarationSchema` from
 * here). The canonical type `EntityRoot` and `EntityRootSchema` live in
 * `pack.ts`; the two enums must stay in lockstep (the pack-schema tests
 * exercise both and would fail if they drifted).
 */
const ENTITY_ROOT_VALUES = [
  'location',
  'character',
  'faction',
  'event',
  'item',
  'lore',
] as const;
type EntityRootLocal = (typeof ENTITY_ROOT_VALUES)[number];

/**
 * II.B.5 (#52) — exported so the `entity-root-closure.test.ts` can
 * diff the predicate-side mirror against the canonical `ENTITY_ROOTS`
 * tuple in `pack.ts`. Application code should import `ENTITY_ROOTS`
 * from `./pack.js`; this binding exists for the closure invariant
 * cross-file check.
 */
export const _INTERNAL_ENTITY_ROOT_VALUES = ENTITY_ROOT_VALUES;

/**
 * The value kinds a `tuple` field may carry. Deliberately a subset of
 * `ValueKind`: tuples do not nest (`tuple`), and to keep the inline
 * tuple-row editor tractable v1 we exclude the media kinds (`asset` /
 * `image` / `file`) and ranges (`real_datetime_range`). Everything a
 * role-qualified relationship needs — `ref`, `text`, `enum`, `number`,
 * the scalar dates — is allowed. (See ADR-0002 for the rationale.)
 */
export const TupleFieldKindSchema = z.enum([
  'text',
  'rich_text',
  'number',
  'boolean',
  'ref',
  'enum',
  'real_date',
  'real_time',
  'real_datetime',
  'game_date',
  'game_datetime',
  'game_time',
]);
export type TupleFieldKind = z.infer<typeof TupleFieldKindSchema>;

/**
 * One option of an `enum` value kind (ADR-0004 §1). Identity is the opaque,
 * minted `enm_<uuidv4>` `id` (`mintVocabId('enum')`); a fact's stored `enum`
 * value is this id, never the display name. `label` is the **mandatory**
 * localized display name (names are freely duplicable — two options may both
 * read "Hostile"); `description` is the optional LLM-grounding / tooltip
 * blurb. Replaces the former `enum_values: string[]` token list, where the
 * raw token was simultaneously identity AND display.
 */
export const EnumValueDeclarationSchema = z
  .object({
    id: EnumValueIdSchema,
    label: RequiredLocalizedNameSchema,
    description: LocalizedStringSchema.optional(),
  })
  .strict();
export type EnumValueDeclaration = z.infer<typeof EnumValueDeclarationSchema>;

/**
 * One named field of a `tuple` value kind. Identity is the opaque, minted
 * `fld_<uuidv4>` `id` (`mintVocabId('field')`) — this also keys the stored
 * record (ADR-0004 §1; replaces the former identity-bearing `name` token).
 * `label` is the **mandatory** localized display name the editor renders.
 * A field may itself be a constrained `ref` (`ref_roots`), an `enum`
 * (`enum_values` — now `EnumValueDeclaration[]`), or a number with a `unit`.
 * `optional` fields may be omitted from the record.
 */
export const TupleFieldDeclarationSchema = z
  .object({
    id: TupleFieldIdSchema,
    label: RequiredLocalizedNameSchema,
    value_kind: TupleFieldKindSchema,
    ref_roots: z.array(z.enum(ENTITY_ROOT_VALUES)).min(1).readonly().optional(),
    enum_values: z.array(EnumValueDeclarationSchema).readonly().optional(),
    unit: UnitIdSchema.optional(),
    optional: z.boolean().optional(),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (field.value_kind === 'enum' && (!field.enum_values || field.enum_values.length === 0)) {
      ctx.addIssue({
        code: 'custom',
        message: `tuple field "${field.id}" is enum but declares no enum_values`,
      });
    }
  });
export type TupleFieldDeclaration = z.infer<typeof TupleFieldDeclarationSchema>;

/**
 * The editor richness for a `rich_text` predicate. `full` = the complete
 * WYSIWYG toolbar; `minimal` = inline marks only (bold/italic/links);
 * `none` = plain single-line/paragraph text with no formatting affordances.
 * Ignored for non-`rich_text` kinds. Default (absent) is treated as `full`.
 */
export const RichTextModeSchema = z.enum(['full', 'minimal', 'none']);
export type RichTextMode = z.infer<typeof RichTextModeSchema>;

/**
 * Optional human labels for the two states of a `boolean` predicate
 * (e.g. "Walled" / "Open" instead of "Yes" / "No"). Ignored for
 * non-boolean kinds.
 */
export const BooleanLabelsSchema = z
  .object({
    true_label: LocalizedStringSchema,
    false_label: LocalizedStringSchema,
  })
  .strict();
export type BooleanLabels = z.infer<typeof BooleanLabelsSchema>;

/**
 * Upload/format constraints for `image` / `file` / `asset` predicates.
 * `accept` is a list of MIME types (or `image/*`-style wildcards);
 * `max_bytes` caps upload size; `buckets` names selectable format groups
 * the file picker offers (e.g. `["document", "audio"]`). Advisory at the
 * substrate boundary — the upload surface enforces, the auditor warns.
 */
export const MediaConstraintsSchema = z
  .object({
    accept: z.array(z.string().min(1)).readonly().optional(),
    max_bytes: z.number().int().positive().optional(),
    buckets: z.array(z.string().min(1)).readonly().optional(),
  })
  .strict();
export type MediaConstraints = z.infer<typeof MediaConstraintsSchema>;

/** Default accepted formats for the `image` kind (PNG / WEBP / JPEG). */
export const DEFAULT_IMAGE_ACCEPT = ['image/png', 'image/webp', 'image/jpeg'] as const;

/**
 * PredicateDeclaration per 03-information-model.md §5. Packs declare these
 * to bind a predicate name to its semantics: which value kinds it accepts,
 * which merge semantics the server applies to stale writes, and which
 * historical names refer to the same predicate.
 *
 * `enum_values` — required when `value_kind` includes `'enum'`. Lists the
 * closed set of allowed string values. Open question (#28 PR): closed enum
 * vs free-form. We ship the scaffolding now; campaign predicates that
 * deserve a free-form posture (e.g. `campaign_kind`) declare `text` instead.
 *
 * `ref_root` — optional refinement for *single-root* `ref`-kind predicates
 * (e.g. `campaign.canonical_calendar` → `lore`).
 *
 * `ref_roots` — optional refinement for *polymorphic* `ref`-kind predicates
 * that accept any of several entity roots (e.g. `governed.ruled_by` accepts
 * `character | faction`; `scheduled.participants` accepts `character |
 * faction`; `magical.bound_to` accepts the full set). Spec calls this
 * `ref_types` (`03a-entities.md §6`); we keep the singular `ref_root` for
 * back-compat and add `ref_roots` (plural) for the multi-root shape
 * introduced in II.B (#48-#51). At most one of the two may be set on any
 * declaration. At Phase 1 scope both fields are metadata — the substrate's
 * `validateValue` does not enforce ref-root membership; the auditor /
 * `diagnoseRef` helper emits a console-level advisory when a fact's
 * referent root is not in the declared set (`03a-entities.md §6`: "the GM
 * may override; the fact persists").
 *
 * Closed-set type-narrowing is II.B.5.
 */
export const PredicateDeclarationSchema = z
  .object({
    /**
     * Opaque, immutable, minted id (`pred_<uuidv4>`, ADR-0004 §1). Authored
     * by the minter (`mintVocabId('predicate')`), never typed by a human or
     * the LLM; decoupled from naming, never user-facing. Kept as a validated
     * string to stay JSON-serialisable through the op log.
     */
    id: PredicateIdSchema,
    value_kind: z.array(ValueKindSchema).min(1).readonly(),
    merge: z.enum(['rebase', 'lww']),
    /**
     * Optional **name-history** only (ADR-0004 §1 / Consequences). With
     * opaque ids, identity never changes on rename, so `previous_names` is
     * no longer identity-bearing — it survives purely as former display
     * names for search/recall. NOT used for alias resolution (the projection
     * canon map and the registry alias chain are removed by #172).
     */
    previous_names: z.array(z.string().min(1)).readonly().default([]),
    /**
     * **Valid-time uniqueness key** (ADR-0005 §2) — the grouping within which
     * two segments may not overlap in valid time. This is the SINGLE
     * multiplicity knob: the former `cardinality` (`single | many`) and
     * `repeatable` flags are gone (ADR-0005 §2); their three-from-one-concern
     * overlap collapses into this one field.
     *
     * Interpretation of the array:
     *  - **`[]` (empty) — key `\u2205`**: at most one segment covers any
     *    in-world day, ever (the classic single-valued, time-varying
     *    predicate; e.g. `ruled_by`). Succeeding the same predicate over time
     *    is fine; two segments overlapping in valid time is the uniqueness
     *    error.
     *  - **`['object']` — key `{object}`**: one segment *per distinct object
     *    value*. Set semantics fall out for free — `alias = Bob` and
     *    `alias = Carol` coexist (different values); `alias = Bob` asserted
     *    twice-overlapping is the redundancy the auditor flags. The literal
     *    token {@link OBJECT_UNIQUENESS_TOKEN} (`'object'`) means "the fact's
     *    value itself" rather than a tuple-field id.
     *  - **`[fld_\u2026, \u2026]` — key `{role}` / tuple-field key**: one
     *    segment per distinct combination of the named `tuple_fields` ids
     *    (e.g. a `government` tuple keyed by its `role` field — one holder per
     *    role at a time; different roles coexist).
     *
     * Absent \u21d2 the predicate is not valid-time segmented (it folds to a
     * single head as before; the editor stays the default single field —
     * progressive disclosure).
     */
    uniqueness_key: z.array(z.string().min(1)).readonly().optional(),
    enum_values: z.array(EnumValueDeclarationSchema).readonly().optional(),
    ref_root: z.enum(ENTITY_ROOT_VALUES).optional(),
    ref_roots: z.array(z.enum(ENTITY_ROOT_VALUES)).min(1).readonly().optional(),
    /**
     * Optional display label for the INVERSE of a `ref`-kind relationship
     * (#backlinks). A directed fact `A {predicate} B` is stored once; the
     * target B surfaces it as a computed backlink, and `reciprocal` is how
     * that backlink reads — e.g. `holds` → "held by", `liege` → "sworn by",
     * `located_in` → "contains". Self-reciprocal relations (rival, ally,
     * kin) just repeat the forward sense. Purely a label — no inverse fact
     * is materialised; backlinks stay computed from the single forward fact.
     * Ignored for non-ref predicates.
     */
    reciprocal: z.string().min(1).optional(),
    /**
     * II.B.5 (#52) — `player_authority: true` flags a predicate as
     * authored from the player perspective. `visited` is the seed
     * case: Characters track their own visited locations, and the
     * auditor lets player-written facts on these predicates stand
     * without GM ratification scrutiny (07-session-experience.md §8).
     * Phase 1 shipping is metadata only — the policy gate that
     * consumes this flag lands with the auditor v1 (Phase 2).
     */
    player_authority: z.boolean().optional(),
    /**
     * **Mandatory** human-facing display name (ADR-0004 §1). Promoted from
     * optional now that the id is opaque — the raw id is never user-facing,
     * so a name in at least the default locale is required. Must carry a
     * non-empty default-locale string (`RequiredLocalizedNameSchema`); names
     * are freely duplicable (identity is the id). The `description` stays
     * optional — an LLM-facing grounding blurb / predicate-row tooltip
     * (`30-design-system.md §7.8`, `03a-entities.md §5`).
     */
    label: RequiredLocalizedNameSchema,
    description: LocalizedStringSchema.optional(),
    /**
     * The bulk-reveal facet this predicate's facts join by default
     * (`03a-entities.md §5/§11`). References a `FacetTemplate.id` declared by
     * one of the entity's traits (`Trait.default_facets`). Drives the §7.10
     * two-tier reveal: facts sharing a facet reveal together. Optional — a
     * predicate with no `default_facet` is only ever revealed per-fact.
     */
    default_facet: z.string().min(1).optional(),
    /**
     * For `number` predicates: the measurement `dimension` scoping which
     * units the picker offers, and the default authoring `unit` id. Both are
     * opaque minted ids (`dim_…` / `unit_…`, ADR-0007 §2) referencing
     * `units.ts` definitions resolved at campaign scope — never readable
     * tokens, never authored by hand or by the LLM.
     */
    dimension: DimensionIdSchema.optional(),
    unit: UnitIdSchema.optional(),
    /** For `boolean` predicates: optional labels for the two states. */
    boolean_labels: BooleanLabelsSchema.optional(),
    /** For `rich_text` predicates: editor richness (full / minimal / none). */
    rich_text_mode: RichTextModeSchema.optional(),
    /** For `tuple` predicates: the named field shape (required, non-empty). */
    tuple_fields: z.array(TupleFieldDeclarationSchema).min(1).readonly().optional(),
    /** For `image` / `file` / `asset` predicates: upload/format constraints. */
    media: MediaConstraintsSchema.optional(),
    /**
     * `featured: true` promotes this predicate into the entities-list
     * summary columns and the entity header (`30-design-system.md §7.2`).
     * Pack/GM-curated; there is no automatic heuristic.
     */
    featured: z.boolean().optional(),
  })
  .strict()
  .superRefine((decl, ctx) => {
    if (decl.value_kind.includes('enum')) {
      if (!decl.enum_values || decl.enum_values.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: `predicate "${decl.id}" declares "enum" value_kind but has no enum_values`,
        });
      }
    }
    if (decl.value_kind.includes('tuple')) {
      if (!decl.tuple_fields || decl.tuple_fields.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: `predicate "${decl.id}" declares "tuple" value_kind but has no tuple_fields`,
        });
      } else {
        const seen = new Set<string>();
        for (const field of decl.tuple_fields) {
          if (seen.has(field.id)) {
            ctx.addIssue({
              code: 'custom',
              message: `predicate "${decl.id}" has duplicate tuple field "${field.id}"`,
            });
          }
          seen.add(field.id);
        }
      }
    }
    if (decl.ref_root !== undefined && decl.ref_roots !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: `predicate "${decl.id}" sets both ref_root (single) and ref_roots (multi) — pick one`,
      });
    }
  });
export type PredicateDeclaration = z.infer<typeof PredicateDeclarationSchema>;

/**
 * The reserved `uniqueness_key` token meaning "key on the fact's own object
 * value" (ADR-0005 §2, the `{object}` case). It is NOT a tuple-field id; the
 * resolver special-cases it. `alias`/`ally` declare `['object']` to get
 * set-per-value semantics (one segment per distinct value; distinct values
 * coexist).
 */
export const OBJECT_UNIQUENESS_TOKEN = 'object';

/**
 * Does this predicate use valid-time segments (ADR-0005)? True when a
 * `uniqueness_key` is declared (including the empty `∅` key). Drives whether
 * the editor offers the correct-vs-change fork + the timeline strip. A
 * predicate with no `uniqueness_key` renders exactly as before (progressive
 * disclosure).
 */
export function isSegmentedPredicate(decl: PredicateDeclaration | null | undefined): boolean {
  return !!decl && Array.isArray(decl.uniqueness_key);
}

/**
 * True when the predicate's uniqueness key is `∅` (the empty array) — "at
 * most one segment covers any in-world day" (`ruled_by`).
 */
export function hasEmptyUniquenessKey(decl: PredicateDeclaration | null | undefined): boolean {
  return !!decl && Array.isArray(decl.uniqueness_key) && decl.uniqueness_key.length === 0;
}

/**
 * True when the predicate's uniqueness key is `{object}` (`['object']`) — one
 * segment per distinct object value; distinct values coexist (`alias`, `ally`).
 */
export function hasObjectUniquenessKey(decl: PredicateDeclaration | null | undefined): boolean {
  return (
    !!decl &&
    Array.isArray(decl.uniqueness_key) &&
    decl.uniqueness_key.length === 1 &&
    decl.uniqueness_key[0] === OBJECT_UNIQUENESS_TOKEN
  );
}


/**
 * Resolve a declaration's effective set of allowed ref roots. Returns the
 * empty array when neither `ref_root` nor `ref_roots` is set (i.e. the
 * predicate is not a ref, or accepts any root — the auditor emits no
 * advisory in that case).
 */
export function allowedRefRoots(decl: PredicateDeclaration): readonly EntityRootLocal[] {
  if (decl.ref_roots && decl.ref_roots.length > 0) return decl.ref_roots;
  if (decl.ref_root) return [decl.ref_root];
  return [];
}

/**
 * A console-level diagnostic per `03a-entities.md §6`. The auditor emits
 * one of these when a ref-kind fact points at an entity whose root is not
 * in the declaring predicate's allowed set. Substrate writers should NOT
 * throw on a non-null diagnostic — the spec is explicit that the fact
 * persists and entity-graph queries treat the edge as valid; the
 * diagnostic is advisory only.
 */
export interface RefRootDiagnostic {
  predicate: string;
  expected: readonly EntityRootLocal[];
  actual: EntityRootLocal;
  message: string;
}

/**
 * Returns a structured diagnostic when `referentRoot` is not in the
 * predicate's declared ref-root set; returns `null` when the ref is
 * acceptable (or the predicate carries no ref-root constraint at all).
 *
 * Intentionally pure — callers wire the diagnostic into their preferred
 * channel (`console.warn`, a structured audit log, the UI's auditor pane,
 * etc).
 */
export function diagnoseRef(
  decl: PredicateDeclaration,
  referentRoot: EntityRootLocal,
): RefRootDiagnostic | null {
  const expected = allowedRefRoots(decl);
  if (expected.length === 0) return null;
  if (expected.includes(referentRoot)) return null;
  return {
    predicate: decl.id,
    expected,
    actual: referentRoot,
    message: `predicate "${decl.id}" expects ref to one of [${expected.join(', ')}] but got "${referentRoot}"`,
  };
}

/**
 * Resolve a `LocalizedString` to its default-locale (`en`) string, or
 * `null` when no default-locale name is present. Used to build the
 * registry's name→candidates index (ADR-0004 §1): a declaration is indexed
 * under its current default-locale name plus every default-locale entry in
 * its `previous_names` history. A bare-string `LocalizedString` is itself
 * the default-locale string; a record resolves its `DEFAULT_LOCALE` key.
 */
export function defaultLocaleName(value: LocalizedString): string | null {
  if (typeof value === 'string') return value.length > 0 ? value : null;
  const v = value[DEFAULT_LOCALE];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * A registry of predicate declarations keyed purely by their **opaque,
 * immutable id** (ADR-0004 §1). `lookup(id)` is a direct id lookup — there
 * is **no alias-canonicalization path**: with opaque ids, identity never
 * changes on rename, so the former `previous_names`-as-identity mechanism
 * is gone. `previous_names` survives only as optional *name*-history, and
 * (like the current name) feeds the non-identity-bearing `byName` candidate
 * index for human/search recall. Deliberate cross-vocabulary unification —
 * "this homebrew predicate IS core's population" — is an explicit
 * alias/merge meta-op (ADR-0004 §8, ticket #178), not a registry concern.
 *
 * **Names are freely duplicable** (ADR-0004 §1): two predicates may share a
 * name under distinct ids, so name resolution returns *all* matching
 * candidates, never a unique map.
 */
export class PredicateRegistry {
  private readonly byId = new Map<string, PredicateDeclaration>();

  add(declaration: PredicateDeclaration): void {
    this.byId.set(declaration.id, declaration);
  }

  /**
   * Direct lookup by opaque id. Returns null if no declaration carries
   * that id. There is no name/alias fallback — identity is the id.
   */
  lookup(id: string): PredicateDeclaration | null {
    return this.byId.get(id) ?? null;
  }

  /** Does the registry hold a declaration with this opaque id? */
  has(id: string): boolean {
    return this.byId.has(id);
  }

  /** All registered declarations (insertion order). */
  list(): PredicateDeclaration[] {
    return [...this.byId.values()];
  }

  /**
   * Name → candidate declarations (ADR-0004 §1). Returns **every**
   * declaration whose current default-locale name OR any default-locale
   * `previous_names` entry equals `name` — duplicates are allowed and
   * expected, so this is a candidate list, never a unique map. The match
   * is exact on the resolved default-locale string. Returns an empty array
   * when nothing matches. Insertion order is preserved.
   *
   * This is the *non-identity-bearing* replacement for the removed
   * `aliasToId`/`aliasMap()`: it serves search/disambiguation UIs and the
   * LLM `find_predicate` tool (ADR-0004 §7), and must never be used to
   * canonicalise a fact's stored predicate id (which is already opaque).
   */
  byName(name: string): PredicateDeclaration[] {
    const out: PredicateDeclaration[] = [];
    for (const decl of this.byId.values()) {
      if (defaultLocaleName(decl.label) === name) {
        out.push(decl);
        continue;
      }
      if (decl.previous_names.includes(name)) {
        out.push(decl);
      }
    }
    return out;
  }
}
